import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, User } from 'lucide-react';
import { SEO } from '@/components/SEO';

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getBlogPost(slug)
      .then(r => setPost(r.data))
      .catch(() => navigate('/blog'))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-pulse">
      <div className="h-8 bg-gray-200 rounded w-48 mb-6" />
      <div className="aspect-video bg-gray-200 rounded-3xl mb-6" />
      <div className="space-y-3"><div className="h-6 bg-gray-200 rounded w-3/4" /><div className="h-4 bg-gray-200 rounded w-full" /></div>
    </div>
  );
  if (!post) return null;

  const renderContent = (content) => {
    return content.split('\n').map((line, i) => {
      if (line.startsWith('## ')) {
        return <h2 key={i} className="font-['Nunito'] font-bold text-xl text-[#1A1D20] mt-8 mb-3">{line.replace('## ', '')}</h2>;
      }
      if (line.startsWith('- ')) {
        return <li key={i} className="text-[#5C636A] leading-relaxed ml-4">{line.replace('- ', '')}</li>;
      }
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="text-[#5C636A] leading-relaxed mb-2">{line}</p>;
    });
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in" data-testid="blog-post-page">
      <SEO
        title={post.title}
        description={post.excerpt}
        keywords={post.tags?.join(', ') || 'dog care tips, puppy training guide, dog nutrition advice'}
        type="article"
        image={post.image_url}
        canonical={`/blog/${post.slug}`}
      />
      <button onClick={() => navigate('/blog')} className="flex items-center gap-1 text-sm text-[#5C636A] hover:text-[#1A1D20] mb-6 transition-colors" data-testid="blog-back-button">
        <ArrowLeft className="w-4 h-4" /> Back to Blog
      </button>

      <article>
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Badge className="bg-orange-50 text-[#F5A623] border-0 rounded-full text-xs font-medium px-3 py-1">
              {post.category}
            </Badge>
            <span className="text-xs text-[#5C636A] flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {new Date(post.created_at).toLocaleDateString()}
            </span>
            <span className="text-xs text-[#5C636A] flex items-center gap-1">
              <User className="w-3 h-3" /> {post.author}
            </span>
          </div>
          <h1 className="font-['Nunito'] font-extrabold text-3xl sm:text-4xl text-[#1A1D20] tracking-tight leading-tight" data-testid="blog-post-title">
            {post.title}
          </h1>
        </div>

        {post.image_url && (
          <div className="aspect-video rounded-3xl overflow-hidden mb-8 shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
            <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}

        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {post.tags.map((tag, i) => (
              <Badge key={i} className="bg-gray-100 text-[#5C636A] border-0 rounded-full text-xs">
                #{tag}
              </Badge>
            ))}
          </div>
        )}

        <div className="prose max-w-none">
          {renderContent(post.content)}
        </div>
      </article>
    </div>
  );
}
